import {EditorState, Compartment, StateEffect} from '@codemirror/state'
import {dropCursor, EditorView, placeholder as placeholderExtension, type ViewUpdate} from '@codemirror/view'
import {type RefObject, useEffect, useLayoutEffect, useMemo, useRef} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import {basicSetup} from '../extensions/basic-setup'
import {getTheme} from '../themes/github-theme'
import type {CodeMirrorProps} from '../CodeMirror'
import {spacingControls} from '../extensions/spacing-controls'
import {fileUploadListener} from '../extensions/file-upload'
import {secretDetectedListener} from '../extensions/secret-detected'
import {dataHpc} from '../extensions/data-hpc'
import {scrollerAttributes} from '../extensions/scroller-attributes'
import {readOnly, externalUpdate} from '../extensions/readonly'
import {focusTrapToggle} from '../extensions/focus-trap-toggle'
import {computeMinimalChange} from '../utils'
import {loadLanguage} from './load-language'
import {selectionListener} from '../extensions/selection-listener'
import {
  addSelectionAttachmentGutters,
  addSelectionAttachmentLines,
  clearSelectionAttachments,
  selectionAttachmentMarkField,
  selectionAttachmentGutterField,
  selectionAttachmentLineField,
} from '../decorations/selection-attachment-decorations'

export interface UseCodeMirror extends CodeMirrorProps {
  parentRef: RefObject<HTMLElement | null>
}

export function useCodeMirror({
  extensions = [],
  fileName = '',
  onChange,
  spacing,
  isHpc,
  isReadOnly,
  enableFileUpload,
  parentRef,
  placeholder = 'Enter file contents here',
  ariaLabelledBy,
  value,
  width = '',
  height = '',
  minHeight = '',
  hideHelpUntilFocus,
  hideHelp,
  onCreateEditor,
  onDestroyEditor,
  selectionListenerOptions,
  selectionAttachmentRange,
}: UseCodeMirror) {
  const viewRef = useRef<EditorView | undefined>(undefined)
  const languageCompartment = useMemo(() => {
    return new Compartment()
  }, [])

  const autoCompleteCompartment = useMemo(() => {
    return new Compartment()
  }, [])

  const themeCompartment = useMemo(() => {
    return new Compartment()
  }, [])

  // Stabilize onChange via ref — prevents updateListener → managedExtensions →
  // StateEffect.reconfigure cascade on every keystroke. Always on: no behavioral change.
  const onChangeRef = useRef(onChange)
  useLayoutEffect(() => {
    onChangeRef.current = onChange
  })

  // Deferred onChange + streaming guards, gated so we can roll back without a deploy.
  // When enabled:
  //   • doc.toString() runs in a setTimeout(0) macrotask, out of the INP critical path.
  //   • lastInternalValueRef skips redundant external-value dispatches after typing.
  //   • hasPendingFlushRef prevents streaming updates from clobbering in-flight edits.
  const inpOptimizationsEnabled = isFeatureEnabled('codemirror_inp_optimizations')
  const lastInternalValueRef = useRef(value)
  const hasPendingFlushRef = useRef(false)
  const flushTimerRef = useRef(0)

  // Stable update listener — [] / [inpOptimizationsEnabled] deps so it never causes
  // managedExtensions to rebuild on onChange identity change.
  // The deferred path is only active when inpOptimizationsEnabled; otherwise onChange
  // is called synchronously (original behavior).
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const updateListener = useMemo(
    () =>
      // eslint-disable-next-line react-hooks/refs
      EditorView.updateListener.of((update: ViewUpdate) => {
        if (update.docChanged) {
          if (inpOptimizationsEnabled) {
            hasPendingFlushRef.current = true
            const updatedState = update.state
            window.clearTimeout(flushTimerRef.current)
            flushTimerRef.current = window.setTimeout(() => {
              hasPendingFlushRef.current = false
              const newDoc = updatedState.doc.toString() || ''
              lastInternalValueRef.current = newDoc
              onChangeRef.current(newDoc)
            }, 0)
          } else {
            onChangeRef.current(update.state.doc.toString() || '')
          }
        }
      }),
    [inpOptimizationsEnabled],
  )

  const editorSizingTheme = useMemo(
    () =>
      EditorView.theme({
        '&': {
          height,
          minHeight,
          width,
        },
      }),
    [height, minHeight, width],
  )

  const managedExtensions = useMemo(() => {
    const exts = [
      ...basicSetup,
      updateListener,
      editorSizingTheme,
      placeholderExtension(placeholder),
      ...spacingControls(spacing),
      secretDetectedListener(),
      languageCompartment.of([]),
      autoCompleteCompartment.of([]),
      themeCompartment.of([getTheme()]),
      scrollerAttributes(ariaLabelledBy),
      EditorView.contentAttributes.of({
        'aria-labelledby': `${ariaLabelledBy} focus-trap-help-panel`,
      }),
      EditorView.editorAttributes.of({
        class: hideHelpUntilFocus ? 'hide-help-until-focus' : '',
      }),
      EditorView.editorAttributes.of({
        class: hideHelp ? 'hide-help' : '',
      }),
    ]

    if (enableFileUpload) {
      exts.push(dropCursor())
      exts.push(fileUploadListener())
    }

    if (isHpc) {
      exts.push(dataHpc())
    }

    if (isReadOnly) {
      exts.push(readOnly())
    } else {
      exts.push(focusTrapToggle(hideHelp))
    }

    if (selectionListenerOptions) {
      exts.push(selectionListener(selectionListenerOptions))
    }

    // Only enable decorated selection if consumer opted in (undefined disables; null means enabled but cleared)
    if (selectionAttachmentRange !== undefined) {
      exts.push(selectionAttachmentMarkField, selectionAttachmentGutterField, selectionAttachmentLineField)
    }

    exts.push(...extensions)

    return exts
  }, [
    updateListener,
    editorSizingTheme,
    placeholder,
    spacing,
    languageCompartment,
    autoCompleteCompartment,
    themeCompartment,
    ariaLabelledBy,
    hideHelpUntilFocus,
    hideHelp,
    enableFileUpload,
    isHpc,
    isReadOnly,
    selectionListenerOptions,
    selectionAttachmentRange,
    extensions,
  ])

  useEffect(() => {
    const parent = parentRef.current
    if (!parent) return

    const state = EditorState.create({
      doc: value,
      extensions: managedExtensions,
    })

    const view = new EditorView({
      state,
      parent,
    })

    onCreateEditor?.(view)

    viewRef.current = view

    return () => {
      window.clearTimeout(flushTimerRef.current)
      view.destroy()

      onDestroyEditor?.(view)
    }
    // right now - only run once to initialize
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (viewRef.current) {
      viewRef.current.dispatch({effects: autoCompleteCompartment.reconfigure([])}) // reset autocomplete
      viewRef.current.dispatch({effects: StateEffect.reconfigure.of(managedExtensions)})
    }
  }, [autoCompleteCompartment, managedExtensions])

  // update the value if it changes externally
  useEffect(() => {
    if (viewRef.current && value !== undefined) {
      if (inpOptimizationsEnabled) {
        // Skip when React is just echoing back the value the editor already committed
        if (value === lastInternalValueRef.current) return

        // Skip if the editor has pending unflushed internal edits — the deferred
        // onChange will deliver the user's content shortly, and applying the (stale)
        // external value here would clobber the in-flight edit.
        if (hasPendingFlushRef.current) return
      }

      const currentValue = viewRef.current.state.doc.toString() || ''
      const newValue = value || ''

      if (newValue !== currentValue) {
        // Calculate minimal change to avoid scroll jitter
        const changes = computeMinimalChange(currentValue, newValue)

        if (changes.length > 0) {
          if (inpOptimizationsEnabled) {
            lastInternalValueRef.current = newValue
          }
          viewRef.current.dispatch({
            changes,
            annotations: [externalUpdate.of(true)],
          })
        }
      }
    }
  }, [value, inpOptimizationsEnabled])

  // Apply decorated selection effect when range changes
  useEffect(() => {
    if (viewRef.current && selectionAttachmentRange !== undefined) {
      if (selectionAttachmentRange === null) {
        // Clear all decorations
        viewRef.current.dispatch({effects: clearSelectionAttachments.of()})
      } else {
        // Apply all line and gutter decorations for the range
        viewRef.current.dispatch({
          effects: [
            addSelectionAttachmentGutters.of(selectionAttachmentRange),
            addSelectionAttachmentLines.of(selectionAttachmentRange),
          ],
        })
      }
    }
  }, [selectionAttachmentRange])

  useEffect(() => {
    loadLanguage({
      autoCompleteCompartment,
      fileName,
      languageCompartment,
      managedExtensions,
      themeCompartment,
      view: viewRef.current,
    })
  }, [fileName, languageCompartment, autoCompleteCompartment, managedExtensions, themeCompartment])

  return viewRef
}
